import { useQuery } from '@tanstack/react-query';
import { axiosGetV3 } from '../../api/axios-handler';

export interface PackageDetailsResponse {
  archived_at: any;
  benefits_description: string;
  benefits_description_col_1: string;
  benefits_description_col_2: string;
  branding_configuration: BrandingConfiguration;
  can_manage_dependents: boolean;
  client: Client;
  code: string;
  collect_user_address: boolean;
  dependents_configuration: DependentsConfiguration;
  external_plan_id: number;
  force_web_app: any;
  id: number;
  name: string;
  package_products: PackageProduct[];
  subscriptions: any[];
  theme_name: any;
}

export interface BrandingConfiguration {
  button_label: string;
  enabled: boolean;
  image_url: string;
  placements: string[];
  type: string;
  url: string;
  video_url: any;
}

export enum VerticalKeys {
  HIGHER_EDUCATION = 'higher_education',
  EMPLOYER = 'employer',
  SENIOR = 'senior',
  HEALTHCARE = 'healthcare',
  FAITH_BASED = 'faith_based',
}

export interface Vertical {
  id: number;
  key: VerticalKeys;
  name: string;
  description: string;
}

// TODO this type is also defined in useUser.tsx and usePackageDetails.tsx.
// These should be consolidated into a single declaration.
export interface Client {
  id: number;
  vertical: Vertical;
  landing_page_content: any;
  logo_background_file_url: any;
  logo_file_url: string;
  logo_overlay_file_url: any;
  member_self_enrollment: boolean;
  name: string;
  promo_configuration: PromoConfiguration;
  sso_configuration?: any;
  video_configuration: VideoConfiguration;
  landing_page_logo_file_url: string;
  landing_page_message: string;
  learn_more_redirect_url: string;
  benefits_description: string;
  benefits_description_2: string;
  welcome_video_vimeo_id: string;
  use_global_resources: boolean;
  enabled_resource_ids: number[];
  powered_by_header_file_url: string;
  powered_by_header_enabled: boolean;
}

export interface PromoConfiguration {
  button_label: string;
  image_url: string;
  placements: string[];
  type: string;
  url: string;
  video_url: string;
}

export interface VideoConfiguration {
  image_url: string;
  title: string;
  video_url: string;
}

export interface DependentsConfiguration {
  dependent_count: number;
  enabled: boolean;
  members_can_add_dependents: boolean;
}

export interface PackageProduct {
  configuration: Configuration;
  created_at: string;
  id: number;
  package_id: number;
  product: Product;
  product_id: number;
  status: string;
}

export interface Configuration {
  image_file?: string;
  required_points?: number;
  reward_value?: number;
  engagement_points?: number;
  interim_question?: string;
  primary_question?: string;
  hide_payment_tiles?: boolean;
  maximum_minutes_per_user?: number;
  auto_renew_frequency?: string;
}

export interface Product {
  id: number;
  key: string;
  product_type: string;
}

const getClientCode = async (partnerCode: string): Promise<PackageDetailsResponse> => {
  if (partnerCode === 'home') {
    return Promise.reject(new Error('We do not allow this package code.'));
  }
  return await axiosGetV3(`/clients/package_details/${partnerCode}`, {}).then((response) => response.data);
};

interface UseClientCodeProps {
  partnerCode?: string;
}

export const useClientCode = ({ partnerCode }: UseClientCodeProps) =>
  useQuery<PackageDetailsResponse>(['client', partnerCode], () => getClientCode(partnerCode!), {
    enabled: !!partnerCode,
    retry: 1,
  });
